import {
  getPreviewRecipientKey,
  isRegisteredPreviewSend,
  type DecisionEngineDestinationDefinition,
} from "./definition";
import {
  getMappingForUserColumn,
  PARSED_RECS,
  PARSED_USER,
  PREVIEW_TRACE_ID_VARIABLE,
} from "./sync-config-helpers";

export const ajoDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "adobe-experience-platform",
  category: "event",
  generateSyncConfig: ({
    channelConfig,
    channelType,
    campaignId,
    isPreview,
    previewRecipientMode,
  }) => {
    const { recipientType, channelDataColumn, channelDataType } = channelConfig;
    const registeredPreview = isRegisteredPreviewSend({
      isPreview,
      previewRecipientMode,
    });
    const previewRecipientKey = getPreviewRecipientKey(channelConfig);

    // Registered previews lift the trace ID to a flat top-level context key.
    // AJO documents contextual attributes one level under `context` but is
    // ambiguous about deeper nesting, so the published snippet is
    // {{context.AidPreviewTraceId}} rather than a variables.* path.
    const contextTemplate = registeredPreview
      ? `${PARSED_RECS}{% assign parsedRecs = parsedRecs | json_construct: '${PREVIEW_TRACE_ID_VARIABLE}', parsedRecs['variables']['${PREVIEW_TRACE_ID_VARIABLE}'] %}{{ parsedRecs }}`
      : `${PARSED_RECS}{{ parsedRecs }}`;

    const mappings: Array<
      | { type: "standard"; from: string; to: string }
      | { type: "static"; to: string; value: string; valueType: string }
      | { type: "template"; template: string; to: string }
    > = [
      // Sending the account address as the identity (unkeyed registered mode)
      // is safe: AJO auto-creates/reuses the account profile from channelData
      // when the campaign has "Create new profiles" enabled, and fails the
      // unitary call synchronously when it doesn't — a visible failure signal
      // on the preview's failed state.
      registeredPreview && previewRecipientKey
        ? {
            type: "static",
            to: "userId",
            value: previewRecipientKey,
            valueType: "STRING",
          }
        : { type: "standard", from: "external_id", to: "userId" },
      {
        type: "template",
        template: contextTemplate,
        to: "context",
      },
      {
        type: "template",
        template: `${PARSED_USER}{{ parsedUser }}`,
        to: "profile",
      },
    ];

    if (channelType !== "push") {
      // Email/SMS: map channel data column to channelData.emailAddress or channelData.mobilePhoneNumber
      // Push channels omit channelData entirely — AJO resolves device tokens from the profile.
      // Registered mode deliberately keeps this preview mapping. The unitary
      // API delivers to channelData, and it seeds the auto-created account
      // profile.
      const channelIdentifierField = channelDataType
        ? `channelData.${channelDataType}`
        : null;

      if (channelIdentifierField && channelDataColumn) {
        mappings.push(
          isPreview
            ? {
                type: "standard",
                from: "external_id",
                to: channelIdentifierField,
              }
            : getMappingForUserColumn(
                channelDataColumn,
                channelIdentifierField,
              ),
        );
      }
    }

    return {
      type: "trigger",
      campaignId,
      namespace: recipientType,
      mappings,
    };
  },
  syncStrategy: {
    supportsChannelLevelSync: false,
  },
};
